import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { useGetVendorList } from '../hooks/useGetVendorList';
import { IVendorContext } from '../interfaces';
import { VendorPicker } from '../components/VendorPicker';
import Button from '../../../generic-components/Button';
import Heading from '../../../generic-components/Heading';
import { useStore } from '../../../store';
import { STYLES } from '../consts';
import { VendorPresentor } from '../components/VendorPresentor';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { ReactComponent as EditVendorIcon } from '../../../assets/vendor-manager/edit-vendor.svg';
import { ReactComponent as SwitchVendorIcon } from '../../../assets/vendor-manager/switch-vendor.svg';
import { FormTypes, IVendorModel, MoveMoneyTypes } from '../../../pages/UpdateVendor/interfaces';
import { UpdateVendorFormContainer } from '../../../pages/UpdateVendor';
import { CloseButton } from '../../../DesignSystem/LiliCloseButton/CloseButton';
import { FORM_TYPES, FormTypeSwitch } from '../../../pages/Supplier/manageComponents/FormTypeSwitch';
import { ISupplierModel } from '../../../pages/Supplier/types';
import { useCreateSupplier } from '../../../pages/Supplier/hooks/useCreateSupplier';

interface IProps {
  initialVendorId: string;
}

export const VendorContext = createContext<IVendorContext>({ formActionType: '', filterKey: '', selectedVendor: undefined });

export const VendorManager = ({ initialVendorId }: IProps) => {
  /** Refs */
  const elWrapper = useRef<any>(null);
  /** State */
  const [selectedVendorId, setSelectedVendorIdLocal] = useState<string>('');
  const [isVendorPicker, setIsVendorPicker] = useState<boolean>(false);
  const [isVendorForm, setIsVendorForm] = useState<boolean>(false);
  const [filterKey, setFilterKey] = useState<string>('');
  const vendorList = useStore(state => state.vendorList);
  const setVendorList = useStore(state => state.setVendorList);
  const setSelectedVendorId = useStore(state => state.setSelectedVendorId);
  const { t } = useTranslation();
  /** Queries */
  const { refetch: getVendorList, isLoading, isRefetching } = useGetVendorList();
  const { mutateAsync: createSupplier, isLoading: isLoadingSupplier } = useCreateSupplier();
  /** Computed */
  const filteredList = useMemo(() => vendorList?.filter((vendor: IVendorModel) => vendor.name?.toLowerCase().includes(filterKey?.toLowerCase())), [filterKey, vendorList]);
  const selectedVendor = useMemo(() => vendorList?.filter((vendor: IVendorModel) => vendor.supplierId === selectedVendorId)[0], [selectedVendorId, vendorList]);
  const [activeFormType, setActiveFormType] = useState<string>(FORM_TYPES.BUSINESS);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterKey(e.target.value);
  };

  const handleUpdateVendor = (actionType: string) => {
    if (actionType === 'CREATE') {
      setSelectedVendorIdLocal('');
      setSelectedVendorId('');
    }
    setIsVendorPicker(false);
    setIsVendorForm(true);
  };
  const handleVendorSelected = (id: string) => {
    setIsVendorPicker(false);
    setSelectedVendorIdLocal(id);
    setSelectedVendorId(id);
  };

  const getList = (shouldForce?: boolean, vendorId?: string) => {
    setIsVendorForm(false);
    if (!shouldForce && vendorList.length) return;
    getVendorList()
      .then((res) => {
        const list = res.data;
        if (list && !!list.length) {
          setVendorList(list);
          if (vendorId) handleVendorSelected(vendorId);
        } else setVendorList([]);
      });
  };

  const handleSubmit = async (formData: ISupplierModel) => {
    const customerData = await createSupplier({ payload: formData });
    getList(true, customerData?.supplierId || '');
  };

  useEffect(() => {
    if (initialVendorId) {
      handleVendorSelected(initialVendorId);
    }
  }, [initialVendorId]);

  useEffect(() => {
    if (!selectedVendor) return;
    setFilterKey('');
  }, [selectedVendor]);

  useEffect(() => {
    getList(true);
    window.addEventListener('click', (e: MouseEvent) => {
      if (elWrapper.current && e.target) {
        if (!elWrapper.current.contains(e.target)) {
          setIsVendorPicker(false);
        }
      }
      return () => {
        window.removeEventListener('click', () => {});
      };
    });
  }, []);

  return isLoading || isRefetching ? <Skeleton width='100%' height='50px' /> : (
    <div ref={elWrapper} className={STYLES.INDEX.WRAPPER}>
      {!selectedVendor
        ? <input id='manage-vendor-filter' value={filterKey} onChange={handleInput} placeholder={t('MANAGE_VENDOR.PLACE_HOLDER')} onFocus={() => setIsVendorPicker(true)} type='text' autoComplete='off' className={`${STYLES.INDEX.FILTER} ${isVendorPicker ? STYLES.INDEX.FILTER_ACTIVE : STYLES.INDEX.FILTER_INACTIVE}`} />
        : (
          <VendorPresentor vendorId={selectedVendorId} name={selectedVendor.name} email={selectedVendor?.email || ''} onClick={() => setIsVendorPicker(!isVendorPicker)} />
        )}
      {selectedVendor && (
      <div className={STYLES.VENDOR_PRESENTOR.ACTIONS_WRAPPER}>
        <Button
          childElement={(
            <div className='flex items-center gap-[4px]'>
              <EditVendorIcon />
              <Heading title={t('MANAGE_VENDOR.EDIT_VENDOR')} className='font-metricR text-[14px] text-action03 hover:underline' />
            </div>
        )}
          onClick={() => handleUpdateVendor('EDIT')}
          className={STYLES.VENDOR_PRESENTOR.ACTION}
          id='editVendor'
          title=''
        />
        <Heading title='|' className={STYLES.VENDOR_PRESENTOR.DIVIDER} />
        <Button
          childElement={(
            <div className='flex items-center gap-[4px]'>
              <SwitchVendorIcon />
              <Heading title={t('MANAGE_VENDOR.SWITCH_VENDOR')} className='font-metricR text-[14px] text-action03 hover:underline' />
            </div>
            )}
          onClick={() => setIsVendorPicker(true)}
          className={STYLES.VENDOR_PRESENTOR.ACTION}
          id='editVendor'
          title=''
        />
      </div>
      )}
      {isVendorForm && (
        <UserMessage onClick={() => setIsVendorForm(false)} id='vendorEditModal'>

          <div className='bg-bw00 flex flex-col h-[620px] w-[560px] rounded-[12px] px-[4px] pb-[4px] shadow-bottomShadow fadeIn'>
            <div className='py-[20px] flex items-center justify-between border-b border-bw03 px-[24px]'>
              <Heading
                title={selectedVendor ? t('UPDATE_VENDOR') : t('CREATE_VENDOR')}
                className='font-metricR text-[18px] text-bw08'
              />
              <CloseButton onClick={() => setIsVendorForm(false)} id='closeModal' />
            </div>
            <div className='w-full flex items-center justify-center mt-[24px]'>
              <FormTypeSwitch onTabSelection={(formType) => setActiveFormType(formType)} activeTabId={activeFormType} />
            </div>
            <div className='relative grow mt-[12px]'>
              <UpdateVendorFormContainer
                isEditMode
                initialBusinessName={filterKey}
                vendorData={selectedVendor}
                moveType={MoveMoneyTypes.ACH_OUT_TRANSFER}
                isLoading={isLoadingSupplier}
                formType={activeFormType as FormTypes}
                onVendorSubmit={handleSubmit}
                customSubmitTitle={t('CREATE')}
                onVendorDelete={() => {
                }}
              />
            </div>
          </div>

        </UserMessage>
      )}
      {isVendorPicker && <VendorPicker
        onCreateNewVendor={() => handleUpdateVendor('CREATE')}
        filteredKey={filterKey}
        onVendorSelected={handleVendorSelected}
        vendorList={filteredList || []}
      />}
    </div>
  );
};
